import React, { useState, useEffect, useRef } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactSection: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Something went wrong. Please check the fields and try again.');
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', companyName: '', email: '', message: '' });
      setIsSubmitting(false);
      setIsSubmitted(false);
      setIsError(false);
      setErrorMessage('Something went wrong. Please check the fields and try again.');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setErrorMessage('Please fill in all required fields (Name, Email, Message).');
      setIsError(true);
      setIsSubmitted(false);
      return;
    }

    setIsSubmitting(true);
    setIsError(false);
    setErrorMessage('Something went wrong. Please check the fields and try again.'); // Reset error message
    setIsSubmitted(false);

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      console.error("API URL is not defined. Make sure REACT_APP_API_URL is set.");
      setErrorMessage("Configuration error: API endpoint is not available.");
      setIsError(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(responseData.message || 'Failed to send message. Please try again.');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrorMessage('An unexpected error occurred. Please try again later.');
      setIsError(true);
    }
    setIsSubmitting(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out">
      <div ref={modalContentRef} className="bg-gray-900 shadow-xl rounded-lg p-8 md:p-12 max-w-lg w-full relative transform transition-all duration-300 ease-in-out scale-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"
          aria-label="Close contact form"
          disabled={isSubmitting}
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Contact Us
          </h2>
          <p className="mt-3 text-md leading-6 text-blue-300">
            We're friendly! Let's talk about your project.
          </p>
        </div>

        {!isSubmitted && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="modal-name" className="block text-sm font-medium text-gray-300">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="modal-name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-white disabled:opacity-50"
              />
            </div>

            <div>
              <label htmlFor="modal-companyName" className="block text-sm font-medium text-gray-300">
                Company name
              </label>
              <input
                type="text"
                name="companyName"
                id="modal-companyName"
                value={formData.companyName}
                onChange={handleChange}
                disabled={isSubmitting}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-white disabled:opacity-50"
              />
            </div>

            <div>
              <label htmlFor="modal-email" className="block text-sm font-medium text-gray-300">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                id="modal-email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-white disabled:opacity-50"
              />
            </div>

            <div>
              <label htmlFor="modal-message" className="block text-sm font-medium text-gray-300">
                Message <span className="text-red-400">*</span>
              </label>
              <textarea
                name="message"
                id="modal-message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-white disabled:opacity-50"
              />
            </div>
            {isError && (
              <p className="text-sm text-red-400">{errorMessage}</p>
            )}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
        {isSubmitted && (
          <div className="text-center py-6">
            <h3 className="text-xl font-semibold text-green-400">Thank you!</h3>
            <p className="mt-2 text-gray-300">Your message has been sent successfully!</p>
            <button 
              onClick={onClose}
              className="mt-4 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSection; 